/**
 * Projection de la carte de San Andreas (même réglage que le MDT du serveur / Leaflet) :
 * CRS Simple, lat = y GTA, lng = x GTA, transformation (0.02072, 117.3, -0.0205, 172.8).
 * "Pixel monde" = pixel au zoom 0 (tuiles de 256 px, ×2 par niveau de zoom).
 */
export const PROJ_A = 0.02072;
export const PROJ_B = 117.3;
export const PROJ_C = -0.0205;
export const PROJ_D = 172.8;

export const TILE_SIZE = 256;
export const NATIVE_MIN_ZOOM = 3;
export const NATIVE_MAX_ZOOM = 5;
export const VIEW_MIN_ZOOM = 1;
export const VIEW_MAX_ZOOM = 7;

/** limites de déplacement (coordonnées GTA) */
export const GTA_BOUNDS = { minX: -5500, maxX: 5500, minY: -8000, maxY: 8000 };

export const tileStyles = {
    atlas: { label: 'Atlas', ocean: '#0fa8d2', url: 'https://www.gtamap.xyz/mapStyles/styleAtlas/{z}/{x}/{y}.jpg' },
    satellite: { label: 'Satellite', ocean: '#123b67', url: 'https://www.gtamap.xyz/mapStyles/styleSatelite/{z}/{x}/{y}.jpg' },
} as const;
export type TileStyle = keyof typeof tileStyles;

/** GTA -> pixel monde (zoom 0) */
export const gtaToWorld = (x: number, y: number) => ({
    px: PROJ_A * x + PROJ_B,
    py: PROJ_C * y + PROJ_D,
});

/** pixel monde (zoom 0) -> GTA */
export const worldToGta = (px: number, py: number) => ({
    x: (px - PROJ_B) / PROJ_A,
    y: (py - PROJ_D) / PROJ_C,
});

export type MapView = {
    /** zoom continu */
    zoom: number;
    /** centre de la vue en pixels monde */
    cx: number;
    cy: number;
    /** taille du conteneur (px écran) */
    width: number;
    height: number;
};

export const worldToScreen = (v: MapView, px: number, py: number) => {
    const s = 2 ** v.zoom;
    return {
        sx: (px - v.cx) * s + v.width / 2,
        sy: (py - v.cy) * s + v.height / 2,
    };
};

export const screenToWorld = (v: MapView, sx: number, sy: number) => {
    const s = 2 ** v.zoom;
    return {
        px: (sx - v.width / 2) / s + v.cx,
        py: (sy - v.height / 2) / s + v.cy,
    };
};

export const gtaToScreen = (v: MapView, x: number, y: number) => {
    const { px, py } = gtaToWorld(x, y);
    return worldToScreen(v, px, py);
};

export const screenToGta = (v: MapView, sx: number, sy: number) => {
    const { px, py } = screenToWorld(v, sx, sy);
    return worldToGta(px, py);
};

/** mètres GTA -> pixels écran (axe x) */
export const metersToScreen = (v: MapView, meters: number) => meters * PROJ_A * 2 ** v.zoom;

/** borne le centre de la vue dans les limites de la carte */
export const clampCenter = (cx: number, cy: number) => {
    const min = gtaToWorld(GTA_BOUNDS.minX, GTA_BOUNDS.maxY);
    const max = gtaToWorld(GTA_BOUNDS.maxX, GTA_BOUNDS.minY);
    return {
        cx: Math.min(Math.max(cx, min.px), max.px),
        cy: Math.min(Math.max(cy, min.py), max.py),
    };
};

export const clampZoom = (z: number) => Math.min(Math.max(z, VIEW_MIN_ZOOM), VIEW_MAX_ZOOM);

/** Tuiles visibles pour la vue courante */
export const visibleTiles = (v: MapView, style: TileStyle) => {
    const tz = Math.min(Math.max(Math.round(v.zoom), NATIVE_MIN_ZOOM), NATIVE_MAX_ZOOM);
    const tilesPerAxis = 2 ** tz;
    const worldTile = TILE_SIZE / tilesPerAxis; //taille d'une tuile en pixels monde
    const screenTile = worldTile * 2 ** v.zoom;
    const tl = screenToWorld(v, 0, 0);
    const br = screenToWorld(v, v.width, v.height);
    const x0 = Math.max(0, Math.floor(tl.px / worldTile));
    const y0 = Math.max(0, Math.floor(tl.py / worldTile));
    const x1 = Math.min(tilesPerAxis - 1, Math.floor(br.px / worldTile));
    const y1 = Math.min(tilesPerAxis - 1, Math.floor(br.py / worldTile));
    const tiles: { key: string, url: string, left: number, top: number, size: number }[] = [];
    for (let tx = x0; tx <= x1; tx++) {
        for (let ty = y0; ty <= y1; ty++) {
            const { sx, sy } = worldToScreen(v, tx * worldTile, ty * worldTile);
            tiles.push({
                key: `${style}/${tz}/${tx}/${ty}`,
                url: tileStyles[style].url
                    .replace('{z}', String(tz))
                    .replace('{x}', String(tx))
                    .replace('{y}', String(ty)),
                //arrondi + 1px de recouvrement pour éviter les joints visibles
                left: Math.floor(sx),
                top: Math.floor(sy),
                size: Math.ceil(screenTile) + 1,
            });
        }
    }
    return tiles;
};

/** Vue initiale : toute l'île visible */
export const fitView = (width: number, height: number): MapView => {
    const tl = gtaToWorld(-4000, 8000);
    const br = gtaToWorld(4500, -4000);
    const zoom = clampZoom(Math.log2(Math.min(width / (br.px - tl.px), height / (br.py - tl.py))));
    return {
        zoom,
        cx: (tl.px + br.px) / 2,
        cy: (tl.py + br.py) / 2,
        width,
        height,
    };
};
